/**
 * Single source for the inline SVG icons used across the checkout form.
 *
 * Before this, 21 inline <svg> blocks were pasted through DynamicForm with 14 distinct paths
 * (the checkmark alone appeared 4 times), and only 3 of the 21 carried aria-hidden -- so
 * screen readers announced the rest as unlabeled graphics.
 *
 * ACCESSIBILITY DEFAULT: icons are hidden from assistive tech unless you pass a `title`.
 * Almost every icon here sits next to text that already conveys the meaning, so hiding is the
 * correct default and making it the *default* means the accessible path is also the lazy one.
 * Pass `title` only when an icon is the sole carrier of meaning.
 */

/*
  Icon path data. Stroke-based (Heroicons outline) unless noted as `filled`.

  A value may be a single `d` string or an ARRAY of them, rendered as sibling <path>s. The
  array form exists because several redesign glyphs are two strokes -- a camera body plus its
  lens, a shield plus its tick. Where the mockup drew a <circle>, it is expressed here as an
  arc path instead of a second element type, so this component still has exactly one thing it
  knows how to render.
*/
const PATHS = {
  // Outline / stroke icons
  checkCircle: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z',
  // Only chevronDown: the disclosure indicator ROTATES rather than swapping to an up-chevron,
  // because a glyph swap cannot be transitioned. See InfoSheet.
  chevronDown: 'M19 9l-7 7-7-7',

  /*
    Per-question glyphs for sections 3 and 4. Named for the subject, not the question, so a
    reworded question keeps its icon.
  */
  camera: [
    'M3 16.5V6a1.5 1.5 0 011.5-1.5h15A1.5 1.5 0 0121 6v10.5a1.5 1.5 0 01-1.5 1.5h-15A1.5 1.5 0 013 16.5zm2 0l4.5-5 3 3.5 2.5-2.5 4 4',
    // The lens: a circle at (9,9) r1.5, drawn as two arcs.
    'M7.5 9a1.5 1.5 0 103 0 1.5 1.5 0 10-3 0',
  ],
  shieldCheck: ['M12 3l7 4v6c0 4-3 6.5-7 8-4-1.5-7-4-7-8V7l7-4z', 'M9.5 12l1.8 1.8L15 10'],
  document: ['M5 4h11l3 3v13H5V4z', 'M8 10h8M8 13.5h8M8 17h5'],
  barChart: 'M4 19V9m5 10V5m5 14v-7m5 7V7',
  speaker: ['M4 9v6h3l6 4V5L7 9H4z', 'M17 9.5c1 1.5 1 3.5 0 5'],
  accessibility: [
    // The head: a circle at (12,4.5) r1.75.
    'M10.25 4.5a1.75 1.75 0 103.5 0 1.75 1.75 0 10-3.5 0',
    'M4.5 8.5h15M12 8.5v5m0 0l-3.5 6m3.5-6l3.5 6',
  ],
  infoCircle: 'M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
  questionCircle:
    'M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
  warningTriangle:
    'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z',
  calendar: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z',
  tag: 'M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z',
  close: 'M6 18L18 6M6 6l12 12',
  creditCard: 'M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z',
  scale:
    'M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z',
  user: 'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z',
  currency:
    'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1',
  adjustments:
    'M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4',
};

/** Filled icons need fill/no-stroke, so they are kept separate. */
const FILLED_PATHS = {
  checkSmall:
    'M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z',
};

/**
 * @param {object} props
 * @param {string} props.name        key from PATHS / FILLED_PATHS
 * @param {string} [props.className] sizing and color utilities, e.g. "w-5 h-5 text-primary-600"
 * @param {string} [props.title]     accessible name; omit for decorative icons (the default)
 * @param {number} [props.strokeWidth] 2 by default; the mockups use 1.75 for question glyphs
 *                                     and 2.5 for tick marks, which need to read at 14px
 */
export default function Icon({ name, className = 'w-5 h-5', title, strokeWidth = 2 }) {
  const strokePath = PATHS[name];
  const filledPath = FILLED_PATHS[name];

  if (!strokePath && !filledPath) {
    // Loud in development, invisible in production: a typo'd icon name should not blank a
    // section of a checkout page.
    if (process.env.NODE_ENV !== 'production') {
      console.error(`Icon: unknown name "${name}"`);
    }
    return null;
  }

  // Decorative unless a title is supplied. focusable="false" keeps SVGs out of the tab order
  // in browsers that make them focusable by default.
  const a11y = title
    ? { role: 'img', 'aria-label': title }
    : { 'aria-hidden': 'true', focusable: 'false' };

  if (filledPath) {
    return (
      <svg className={className} viewBox="0 0 20 20" fill="currentColor" {...a11y}>
        <path fillRule="evenodd" clipRule="evenodd" d={filledPath} />
      </svg>
    );
  }

  // Normalize to an array so the single-string and multi-stroke forms share one render path.
  const strokePaths = Array.isArray(strokePath) ? strokePath : [strokePath];

  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      {...a11y}
    >
      {strokePaths.map((d) => (
        <path
          key={d}
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={strokeWidth}
          d={d}
        />
      ))}
    </svg>
  );
}

/** Exported for tests and for anything that needs to enumerate available icons. */
export const ICON_NAMES = [...Object.keys(PATHS), ...Object.keys(FILLED_PATHS)];
